export const AUTO_MAP_THRESHOLD = 50;
export const HIGH_CONFIDENCE = 85;

const SAMPLE_ROWS = 50;

const TOKEN_SYNONYMS = {
  mfg: "manufacturer",
  mfgr: "manufacturer",
  mfr: "manufacturer",
  mfrs: "manufacturer",
  mnf: "manufacturer",
  manf: "manufacturer",
  manuf: "manufacturer",
  manufacture: "manufacturer",
  manufacturers: "manufacturer",
  maker: "manufacturer",
  make: "manufacturer",
  brand: "manufacturer",
  no: "number",
  num: "number",
  nr: "number",
  nbr: "number",
  numbers: "number",
  qty: "quantity",
  qnty: "quantity",
  qt: "quantity",
  quantities: "quantity",
  pcs: "quantity",
  ref: "designator",
  refs: "designator",
  reference: "designator",
  references: "designator",
  refdes: "designator",
  des: "designator",
  designators: "designator",
  designation: "designator",
  desig: "designator",
  desc: "description",
  descr: "description",
  cust: "customer",
  client: "customer",
  internal: "customer",
  company: "customer",
  items: "item",
  index: "item",
  alternate: "alt",
  alternative: "alt",
  second: "alt",
  secondary: "alt",
};

const TOKEN_EXPANSIONS = {
  pn: ["part", "number"],
  partno: ["part", "number"],
  partnumber: ["part", "number"],
  mpn: ["manufacturer", "part", "number"],
  cpn: ["customer", "part", "number"],
  itemno: ["item", "number"],
  bomitem: ["item"],
};

// Vocabulary for splitting glued headers like "MFGPARTNUMBER" or "refdes".
const SEGMENT_VOCAB = [
  "manufacturer", "description", "designator", "reference", "quantity",
  "customer", "internal", "number", "part", "name", "item", "mfgr", "mfg",
  "mfr", "qty", "ref", "des", "desc", "cust", "bom", "pn", "no", "num", "line",
].sort((a, b) => b.length - a.length);

const KNOWN_TOKENS = new Set([
  ...Object.keys(TOKEN_SYNONYMS),
  ...Object.values(TOKEN_SYNONYMS),
  ...Object.keys(TOKEN_EXPANSIONS),
  ...SEGMENT_VOCAB,
  "value", "comment", "find", "per", "assembly", "assm", "assy",
]);

const vocabAt = (word, pos) => SEGMENT_VOCAB.find((v) => word.startsWith(v, pos));

const segmentWord = (word) => {
  const parts = [];
  let pos = 0;
  while (pos < word.length) {
    const hit = vocabAt(word, pos);
    if (!hit) return [word];
    parts.push(hit);
    pos += hit.length;
  }
  return parts;
};

export const headerTokens = (header) => {
  const raw = String(header == null ? "" : header)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/#/g, " number ")
    .replace(/\bp\s*[/.]\s*n\b\.?/g, " pn ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (!raw) return [];

  const tokens = [];
  raw.split(" ").forEach((word) => {
    const pieces = KNOWN_TOKENS.has(word) ? [word] : segmentWord(word);
    pieces.forEach((piece) => {
      const expanded = TOKEN_EXPANSIONS[piece] || [TOKEN_SYNONYMS[piece] || piece];
      expanded.forEach((t) => {
        if (!tokens.includes(t)) tokens.push(t);
      });
    });
  });
  return tokens;
};

export const canonicalHeader = (header) => headerTokens(header).join(" ");

const EXCLUDED_TOKENS = new Set([
  "lib", "library", "footprint", "price", "cost", "stock", "datasheet", "url", "link",
]);

const scoreHeader = (tokens) => {
  if (tokens.some((t) => EXCLUDED_TOKENS.has(t))) return {};
  const has = (t) => tokens.includes(t);
  const partish = has("part") || has("number");
  const alt = has("alt") || tokens.some((t) => /^\d+$/.test(t) && t !== "1");
  const scores = {};

  if (has("manufacturer") && partish && !has("customer")) {
    scores.manufacturer_part_number = 95;
  } else if (has("part") && has("number") && !has("customer") && !has("item")) {
    scores.manufacturer_part_number = 65;
  } else if (tokens.length === 1 && has("part")) {
    scores.manufacturer_part_number = 55;
  }

  if (has("manufacturer") && !partish) {
    scores.manufacturer = has("name") || tokens.length === 1 ? 95 : 85;
  }

  if (has("customer") && partish) {
    scores.customer_part_number = 95;
  } else if (has("part") && has("number") && !has("manufacturer") && !has("item")) {
    scores.customer_part_number = 60;
  }

  if (has("quantity")) scores.quantity = 95;
  if (has("designator") && !has("customer")) scores.designator = 95;

  if (has("description")) {
    scores.description = 95;
  } else if (has("value") || has("comment")) {
    scores.description = 65;
  }

  if (has("item") || has("line") || has("find")) {
    scores.item_number = 90;
  } else if (tokens.length === 1 && has("number")) {
    scores.item_number = 75;
  }

  if (alt) {
    Object.keys(scores).forEach((k) => {
      scores[k] -= 25;
    });
  }
  return scores;
};

const DESIGNATOR_REF = "[A-Za-z]{1,4}\\d{1,4}(?:\\s*[-–]\\s*[A-Za-z]{0,4}\\d{1,4})?";
const DESIGNATOR_CELL = new RegExp(
  `^\\s*${DESIGNATOR_REF}(?:\\s*[,;\\s]\\s*${DESIGNATOR_REF})*\\s*[,;]?\\s*$`
);
const DESIGNATOR_PREFIX = /^(R|C|L|U|D|Q|J|P|K|F|T|X|Y|SW|TP|FB|LED|IC|CN|JP|RN|VR|BT|FID|H|MH|S)\d/i;
const INTEGER_CELL = /^\s*\d{1,6}(\.0+)?\s*$/;

const scoreContent = (values) => {
  const cells = values
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
  if (cells.length < 3) return {};

  const ratio = (fn) => cells.filter(fn).length / cells.length;
  const scores = {};

  const designatorRatio = ratio((c) => DESIGNATOR_CELL.test(c));
  const knownPrefixRatio = ratio((c) => DESIGNATOR_PREFIX.test(c));
  const multiRefRatio = ratio((c) => /[,;]|\s\S/.test(c) && DESIGNATOR_CELL.test(c));
  if (designatorRatio >= 0.8 && (knownPrefixRatio >= 0.7 || multiRefRatio >= 0.2)) {
    scores.designator = 60;
  }

  if (ratio((c) => INTEGER_CELL.test(c)) >= 0.9) {
    const nums = cells.map(Number);
    const sequential = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
    if (sequential) {
      scores.item_number = 55;
    } else {
      scores.quantity = 55;
    }
  }

  return scores;
};

const contradicts = (field, values) => {
  const cells = values
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);
  if (cells.length < 3) return false;
  const ratio = (re) => cells.filter((c) => re.test(c)).length / cells.length;

  if (field === "quantity") return ratio(INTEGER_CELL) < 0.5;
  if (field === "designator") return ratio(DESIGNATOR_CELL) < 0.3;
  return false;
};

/**
 * Suggests a target column for each spreadsheet header.
 *
 * @param {string[]} headers  Header row values, in column order.
 * @param {object[]} rows     Data rows (objects keyed like the header row) used for content checks.
 * @param {object[]} columnAliases  Rows from /columnAliases: { code_name, list_of_aliases }.
 * @param {string[]} allowedFields  code_names that exist in the Columns dropdown.
 * @returns {{ field: string, score: number }[]}  One entry per header; field is "ignore" when unmatched.
 */
export const autoMapColumns = (headers, rows = [], columnAliases = [], allowedFields = null) => {
  const allowed = allowedFields ? new Set(allowedFields) : null;
  const aliasLookup = new Map();
  (Array.isArray(columnAliases) ? columnAliases : []).forEach(({ code_name, list_of_aliases }) => {
    if (!code_name || code_name === "ignore") return;
    const list = Array.isArray(list_of_aliases) ? list_of_aliases : [];
    list.forEach((alias) => {
      const key = canonicalHeader(alias);
      if (key && !aliasLookup.has(key)) aliasLookup.set(key, code_name);
    });
  });

  const rowKeys = rows.length ? Object.keys(rows[0]) : [];
  const sample = rows.slice(0, SAMPLE_ROWS);

  const candidates = [];
  headers.forEach((header, index) => {
    const tokens = headerTokens(header);
    const values = sample.map((row) => row[rowKeys[index]]);
    const scores = scoreHeader(tokens);

    Object.keys(scores).forEach((field) => {
      if (contradicts(field, values)) scores[field] -= 30;
    });

    Object.entries(scoreContent(values)).forEach(([field, score]) => {
      scores[field] = scores[field] ? Math.min(100, scores[field] + 5) : score;
    });

    const aliasField = aliasLookup.get(tokens.join(" "));
    if (aliasField) scores[aliasField] = 100;

    Object.entries(scores).forEach(([field, score]) => {
      if (score >= AUTO_MAP_THRESHOLD && (!allowed || allowed.has(field))) {
        candidates.push({ index, field, score });
      }
    });
  });

  candidates.sort((a, b) => b.score - a.score || a.index - b.index);

  const result = headers.map(() => ({ field: "ignore", score: 0 }));
  const usedFields = new Set();
  candidates.forEach(({ index, field, score }) => {
    if (result[index].field !== "ignore" || usedFields.has(field)) return;
    result[index] = { field, score };
    usedFields.add(field);
  });
  return result;
};

const HEADER_SCAN_ROWS = 20;

/**
 * Finds the row that looks most like a header row (for sheets with title lines on top).
 * Returns 0 unless a later row is a clearly better header.
 */
export const detectHeaderRow = (data, columnAliases = []) => {
  const rows = Array.isArray(data) ? data.slice(0, HEADER_SCAN_ROWS) : [];
  let bestIndex = 0;
  let bestStrong = -1;

  rows.forEach((row, index) => {
    const headers = Object.values(row || {});
    const strong = autoMapColumns(headers, [], columnAliases).filter(
      ({ score }) => score >= HIGH_CONFIDENCE
    ).length;
    if (strong > bestStrong) {
      bestStrong = strong;
      bestIndex = index;
    }
  });

  return bestStrong >= 2 ? bestIndex : 0;
};
